import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { Upload, Info, AlertTriangle, HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChipPosSelector, WaferDie } from "./ChipPosSelector";
import {
  toChipPosFile,
  parseChipPosFile,
  type ChipPosHeader,
  type ParsedChipPos,
  type ParsedDie,
} from "./chipPosConverter";
import { useChipPosByMask, useUploadChipPos } from "@/hooks/useChipPos";

/** Mask data required for the editor */
export type ChipPosEditorMaskData = {
  id: string;
  name: string;
  waferDiameterInMm: number;
  dieWidth?: number;
  dieHeight?: number;
  dieOffsetX?: number;
  dieOffsetY?: number;
  referenceDieX?: number;
  referenceDieY?: number;
};

export type ChipPosEditorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Mask data to configure the wafer */
  maskData: ChipPosEditorMaskData;
  /** Optional initial parsed chip_pos data (for editing existing file) */
  initialData?: ParsedChipPos | null;
  /** Optional initial file name (for editing existing file) */
  initialFileName?: string;
  /** Optional callback when selection is confirmed and uploaded */
  onConfirm?: (selectedDies: WaferDie[], fileName: string) => void;
};

// Default values for die dimensions (in µm) - used if not provided by mask
const DEFAULT_DIE_WIDTH = 22310;
const DEFAULT_DIE_HEIGHT = 14870;
const DEFAULT_EDGE_INDENT = 5; // mm
const DEFAULT_STREET = 5; // µm

/** Represents differences between chip_pos header and mask values */
type HeaderMismatch = {
  field: string;
  chipPosValue: string | number;
  maskValue: string | number;
};

/** Compare chip_pos header values with mask values and return mismatches */
function compareHeaderWithMask(
  header: ChipPosHeader,
  maskData: ChipPosEditorMaskData,
  dieWidth: number,
  dieHeight: number,
): HeaderMismatch[] {
  const mismatches: HeaderMismatch[] = [];

  // Compare die width
  if (header.dieWidth !== dieWidth) {
    mismatches.push({
      field: "Die Width (X)",
      chipPosValue: `${header.dieWidth} µm`,
      maskValue: `${dieWidth} µm`,
    });
  }

  // Compare die height
  if (header.dieHeight !== dieHeight) {
    mismatches.push({
      field: "Die Height (Y)",
      chipPosValue: `${header.dieHeight} µm`,
      maskValue: `${dieHeight} µm`,
    });
  }

  // Compare diameter (chip_pos uses µm, mask uses mm)
  const maskDiameterInUm = maskData.waferDiameterInMm * 1000;
  if (header.diameter !== maskDiameterInUm) {
    mismatches.push({
      field: "Wafer Diameter (D)",
      chipPosValue: `${header.diameter} µm`,
      maskValue: `${maskDiameterInUm} µm`,
    });
  }

  return mismatches;
}

export function ChipPosEditorDialog({
  open,
  onOpenChange,
  maskData,
  initialData = null,
  initialFileName = "",
  onConfirm,
}: ChipPosEditorDialogProps) {
  const { trigger: uploadChipPos, isMutating: isUploading } = useUploadChipPos();
  const { data: existingChipPos = [] } = useChipPosByMask(maskData.id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedDieIds, setSelectedDieIds] = useState<string[]>([]);
  const [nameError, setNameError] = useState<string>("");
  const [allDies, setAllDies] = useState<WaferDie[]>([]);
  const [step, setStep] = useState<"select" | "name">("select");
  const [fileName, setFileName] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [_parseError, setParseError] = useState<string | null>(null);
  const [headerMismatches, setHeaderMismatches] = useState<HeaderMismatch[]>([]);

  // Track if we're editing an existing file
  const isEditing = !!initialData && !!initialFileName;
  const originalFileName = initialFileName.replace(/\.chip_pos$/, "");
  const isNameChanged = fileName !== originalFileName;

  // Use mask data if available, otherwise fall back to defaults
  const dieWidth = maskData.dieWidth ?? DEFAULT_DIE_WIDTH;
  const dieHeight = maskData.dieHeight ?? DEFAULT_DIE_HEIGHT;
  const dieOffsetX = maskData.dieOffsetX;
  const dieOffsetY = maskData.dieOffsetY;
  const referenceDieX = maskData.referenceDieX;
  const referenceDieY = maskData.referenceDieY;

  // Initialize from initial data when dialog opens
  useEffect(() => {
    if (open) {
      setStep("select");
      setUploadError(null);
      setParseError(null);
      setHeaderMismatches([]);

      if (initialData) {
        setSelectedDieIds(initialData.selectedDieIds);
        setFileName(initialFileName.replace(/\.chip_pos$/, ""));
      } else {
        setSelectedDieIds([]);
        setFileName("");
      }
    }
  }, [open, initialData, initialFileName]);

  const handleDieClick = (die: WaferDie) => {
    setSelectedDieIds((prev) => {
      const index = prev.indexOf(die.id);
      if (index > -1) {
        // Don't allow deselecting the reference die - it's always first
        if (die.isReference) {
          return prev;
        }
        // Remove if already selected
        return prev.filter((id) => id !== die.id);
      } else {
        // Add to end to maintain order
        return [...prev, die.id];
      }
    });
  };

  const selectedDies = useMemo(() => {
    return selectedDieIds
      .map((id) => allDies.find((die) => die.id === id))
      .filter((die): die is WaferDie => die !== undefined);
  }, [selectedDieIds, allDies]);

  // Auto-select the reference die when dies are calculated (if no initial data)
  useEffect(() => {
    if (!initialData && allDies.length > 0 && selectedDieIds.length === 0) {
      const referenceDie = allDies.find((die) => die.isReference);
      if (referenceDie) {
        setSelectedDieIds([referenceDie.id]);
      }
    }
  }, [allDies, initialData, selectedDieIds.length]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setHeaderMismatches([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = parseChipPosFile(content);

        // Compare header values with mask values
        const mismatches = compareHeaderWithMask(parsed.header, maskData, dieWidth, dieHeight);
        if (mismatches.length > 0) {
          setHeaderMismatches(mismatches);
        }

        // Apply the parsed selection to current wafer
        setSelectedDieIds(parsed.selectedDieIds);

        // Use file name (without extension) as default name
        const nameWithoutExt = file.name.replace(/\.chip_pos$/, "");
        setFileName(nameWithoutExt);
      } catch (error) {
        console.error("Failed to parse chip_pos file:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to parse chip_pos file";
        setParseError(errorMessage);
        toast.error(errorMessage);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file");
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleProceedToName = () => {
    setStep("name");
    // Default filename based on mask name if not already set
    if (!fileName) {
      setFileName(`${maskData.name}_selection`);
    }
  };

  const handleBackToSelect = () => {
    setStep("select");
    setUploadError(null);
  };

  const handleConfirm = async () => {
    if (!fileName.trim()) return;

    const isUpdatingSameFile = isEditing && !isNameChanged;
    if (!isUpdatingSameFile) {
      const trimmedName = fileName.trim().toLowerCase();
      const conflict = existingChipPos.some(
        (cp) => cp.name.replace(/\.chip_pos$/, "").toLowerCase() === trimmedName,
      );
      if (conflict) {
        setNameError(
          "A chip pos file with this name already exists for this mask. Try a different name.",
        );
        return;
      }
    }
    setNameError("");
    setUploadError(null);

    try {
      // Create the chip_pos file header
      const header: ChipPosHeader = {
        name: maskData.name,
        waferSelection: "WAFER_ALL",
        dieWidth: dieWidth,
        dieHeight: dieHeight,
        diameter: maskData.waferDiameterInMm * 1000, // Convert mm to µm
        orientation: "bottom",
      };

      // Convert WaferDie[] to ParsedDie[] format
      const selectionOrderMap = new Map<string, number>();
      selectedDieIds.forEach((id, index) => {
        selectionOrderMap.set(id, index);
      });

      const parsedDies: ParsedDie[] = allDies.map((die) => ({
        columnIndex: die.columnIndex,
        rowIndex: die.rowIndex,
        isMeasurable: true, // All dies in the selector are measurable
        isReference: die.isReference ?? false,
        selectionOrder: selectionOrderMap.has(die.id) ? selectionOrderMap.get(die.id)! : null,
      }));

      // Find reference die ID
      const referenceDie = allDies.find((die) => die.isReference);
      const referenceDieId = referenceDie ? referenceDie.id : null;

      // Create ParsedChipPos object
      const parsedChipPos: ParsedChipPos = {
        header,
        dies: parsedDies,
        selectedDieIds,
        referenceDieId,
      };

      // Generate chip_pos file content
      const chipPosContent = toChipPosFile(parsedChipPos);

      // Create a File object
      const fileNameWithExt = fileName.endsWith(".chip_pos") ? fileName : `${fileName}.chip_pos`;
      const file = new File([chipPosContent], fileNameWithExt, {
        type: "application/octet-stream",
      });

      // Upload using the hook
      await uploadChipPos({
        mask_id: maskData.id,
        file,
      });

      // Success - call onConfirm and close dialog
      if (onConfirm) {
        onConfirm(selectedDies, fileNameWithExt);
      }
      onOpenChange(false);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Failed to upload chip_pos file");
    }
  };

  const handleClearSelection = () => {
    // Keep only the reference die selected (it's always first)
    const referenceDie = allDies.find((die) => die.isReference);
    setSelectedDieIds(referenceDie ? [referenceDie.id] : []);
  };

  const handleSelectAll = () => {
    // Ensure reference die is first, then all other dies
    const referenceDie = allDies.find((die) => die.isReference);
    const otherDieIds = allDies.filter((die) => !die.isReference).map((die) => die.id);
    setSelectedDieIds(referenceDie ? [referenceDie.id, ...otherDieIds] : otherDieIds);
  };

  const dialogTitle =
    step === "select"
      ? isEditing
        ? `Edit Die Selection - ${originalFileName}`
        : `Create Die Selection - ${maskData.name}`
      : "Save Chip Pos File";

  const dialogDescription =
    step === "select"
      ? "Click on dies to select them for measurement. You can also upload an existing chip_pos file to import a selection."
      : isEditing
        ? "Enter a name for your chip_pos file. Using the same name will update the existing file. Using a different name will create a new file."
        : "Enter a name for your chip_pos file. The .chip_pos extension will be added automatically.";

  const isNameChangedClasses = isNameChanged
    ? "bg-blue-50 border-blue-200 text-blue-700"
    : "bg-amber-50 border-amber-200 text-amber-700";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <>
            <div className="space-y-4">
              {/* File Upload Section */}
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Import from file:</span> Upload an existing
                    chip_pos file to load its die selection
                  </div>
                  {/* Chip Pos Format Info */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full p-1 mr-6 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="Learn about chip_pos file format"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96" align="start">
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">Chip Pos File Format</h4>
                        <p className="text-xs text-muted-foreground">
                          A <code>.chip_pos</code> file is an ASCII text document that depicts a
                          wafer layout and indicates which dies to measure, in what order, and where
                          the reference die is located.
                        </p>

                        <div className="text-xs space-y-2">
                          <h5 className="font-medium">Header Values</h5>
                          <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                            <span>
                              <code>C=</code> Mask name
                            </span>
                            <span>
                              <code>W=</code> Wafer selection
                            </span>
                            <span>
                              <code>X=</code> Die width (µm)
                            </span>
                            <span>
                              <code>Y=</code> Die height (µm)
                            </span>
                            <span>
                              <code>D=</code> Wafer diameter (µm)
                            </span>
                            <span>
                              <code>F=</code> Orientation
                            </span>
                          </div>
                        </div>

                        <div className="text-xs space-y-2">
                          <h5 className="font-medium">Map Symbols</h5>
                          <div className="space-y-1 text-muted-foreground">
                            <div>
                              <code className="bg-gray-100 px-1">-</code> Unmeasurable die
                            </div>
                            <div>
                              <code className="bg-gray-100 px-1">+</code> Measurable die (not
                              selected)
                            </div>
                            <div>
                              <code className="bg-gray-100 px-1">+N</code> Selected die with order N
                              (e.g., +1, +2)
                            </div>
                            <div>
                              <code className="bg-gray-100 px-1">o0</code> Reference die (origin,
                              measured first)
                            </div>
                            <div>
                              <code className="bg-gray-100 px-1">&nbsp;</code> (space) Empty
                              position
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground italic border-t pt-2">
                            <strong>Note:</strong> Only ordered notation is supported. Unordered
                            notation will result in an error.
                          </p>
                        </div>

                        <p className="text-xs text-muted-foreground italic border-t pt-2">
                          See{" "}
                          <Link
                            to="/docs/chip-pos-file-format"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            full documentation
                          </Link>{" "}
                          for more details.
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".chip_pos"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" onClick={handleUploadButtonClick}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload file
                  </Button>
                </div>
              </div>

              {/* Header Mismatch Warning */}
              {headerMismatches.length > 0 && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-800">Header Values Mismatch</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    <p className="mb-2">
                      Your chip_pos header values differ from the mask values. This may lead to a
                      different distribution of dies and incorrect die selections. The chip_pos
                      headers will be ignored; the mask values will be used.
                    </p>
                    <div className="text-xs space-y-1">
                      {headerMismatches.map((mismatch) => (
                        <div key={mismatch.field} className="flex gap-2">
                          <span className="font-medium">{mismatch.field}:</span>
                          <span className="line-through text-amber-600">
                            {mismatch.chipPosValue}
                          </span>
                          <span>→</span>
                          <span className="font-medium">{mismatch.maskValue}</span>
                        </div>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Selection Stats */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Selected Dies:</span>
                  <Badge variant={selectedDieIds.length > 0 ? "default" : "secondary"}>
                    {selectedDieIds.length}
                  </Badge>
                  {/* Mask Values Info */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="View mask values used for this wafer"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Mask Values</h4>
                        <p className="text-xs text-muted-foreground">
                          These values from the mask are used to generate the wafer layout:
                        </p>
                        <div className="text-xs space-y-1 border-t pt-2">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Mask Name:</span>
                            <span className="font-medium">{maskData.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Wafer Diameter:</span>
                            <span className="font-medium">{maskData.waferDiameterInMm} mm</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Die Width (X):</span>
                            <span className="font-medium">{dieWidth} µm</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Die Height (Y):</span>
                            <span className="font-medium">{dieHeight} µm</span>
                          </div>
                          {dieOffsetX !== undefined && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Die Offset X:</span>
                              <span className="font-medium">{dieOffsetX} µm</span>
                            </div>
                          )}
                          {dieOffsetY !== undefined && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Die Offset Y:</span>
                              <span className="font-medium">{dieOffsetY} µm</span>
                            </div>
                          )}
                          {referenceDieX !== undefined && referenceDieY !== undefined && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Reference Die:</span>
                              <span className="font-medium">
                                ({referenceDieX}, {referenceDieY})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  {selectedDieIds.length > 0 && (
                    <Button variant="outline" size="sm" onClick={handleClearSelection}>
                      Clear Selection
                    </Button>
                  )}
                </div>
              </div>

              {/* Wafer Visualization */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="w-full h-[500px]">
                  <ChipPosSelector
                    diameter={maskData.waferDiameterInMm}
                    dieWidth={dieWidth}
                    dieHeight={dieHeight}
                    dieOffsetX={dieOffsetX}
                    dieOffsetY={dieOffsetY}
                    referenceDieX={referenceDieX}
                    referenceDieY={referenceDieY}
                    edgeIndent={DEFAULT_EDGE_INDENT}
                    street={DEFAULT_STREET}
                    onDieClick={handleDieClick}
                    onDiesCalculated={setAllDies}
                    selectedDieIds={selectedDieIds}
                    ariaLabel={`Wafer layout for ${maskData.name}`}
                  />
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-white border border-blue-800 rounded" />
                  <span>Unselected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-500 border border-gray-950 rounded" />
                  <span>Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-100 border-2 border-blue-600 rounded" />
                  <span>Reference Die (0:0)</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleProceedToName} disabled={selectedDieIds.length === 0}>
                Continue ({selectedDieIds.length} {selectedDieIds.length === 1 ? "die" : "dies"})
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="chip-pos-name">File Name</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="chip-pos-name"
                    value={fileName}
                    onChange={(e) => {
                      setFileName(e.target.value);
                      if (nameError) setNameError("");
                    }}
                    placeholder="Enter file name"
                    disabled={isUploading}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">.chip_pos</span>
                </div>
                {nameError && <p className="text-sm text-destructive">{nameError}</p>}
              </div>

              <div className="text-sm text-muted-foreground">
                <p>Selected dies: {selectedDieIds.length}</p>
                <p>Mask: {maskData.name}</p>
              </div>

              {/* Info about update vs create */}
              {isEditing && (
                <div className={`p-3 text-sm rounded-md border ${isNameChangedClasses}`}>
                  {isNameChanged ? (
                    <>
                      <span className="font-medium">New file:</span> A new chip_pos file will be
                      created with this name.
                    </>
                  ) : (
                    <>
                      <span className="font-medium">Update existing:</span> The existing chip_pos
                      file "{originalFileName}" will be updated with the new selection.
                    </>
                  )}
                </div>
              )}

              {uploadError && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  {uploadError}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleBackToSelect} disabled={isUploading}>
                Back
              </Button>
              <Button onClick={handleConfirm} disabled={!fileName.trim() || isUploading}>
                {isUploading
                  ? "Saving..."
                  : isEditing && !isNameChanged
                    ? "Update Chip Pos File"
                    : "Create Chip Pos File"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
